import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DeadlineTypeEnum } from '@zaoblako/shared/common/enums/deadline.type.enum';
import { TemplateEntityPeriodicityEnum } from '@zaoblako/shared/common/enums/template.entity.periodicity.enum';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { DocumentTemplateFindInterface } from '@zaoblako/shared/modules/document/dtos/document-template/document-template-find.dto';
import {
  DocumentTemplateFormDto,
  type DocumentTemplateFormInterface
} from '@zaoblako/shared/modules/document/dtos/document-template/document-template-form.dto';
import type { DocumentFindInterface } from '@zaoblako/shared/modules/document/dtos/document/document-find.dto';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DocumentRegistryService } from '@zaoblako/web/core/services/document/document-registry.service';
import { DocumentTemplateService } from '@zaoblako/web/core/services/document/document-template.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { DocumentTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-type-single-select/document-type-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { OperationTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/operation-type-single-select/operation-type-single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { PlanningDeadlineFormComponent } from '@zaoblako/web/shared/dumbs/planning-deadline-form/planning-deadline-form.component';
import { PlanningPeriodicityCardComponent } from '@zaoblako/web/shared/dumbs/planning-periodicity-card/planning-periodicity-card.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TemplateSubjectEditorComponent } from '@zaoblako/web/shared/dumbs/template-subject-editor/template-subject-editor.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-template-form',
  templateUrl: './document-template-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    CheckboxModule,
    JoinPipe,
    FormsModule,
    InputNumberModule,
    ContragentAutoCompleteComponent,
    DirectionSingleSelectComponent,
    DividerModule,
    PlanningDeadlineFormComponent,
    PlanningPeriodicityCardComponent,
    ButtonComponent,
    TemplateSubjectEditorComponent,
    SpinnerComponent,
    DealAutoCompleteComponent,
    UnitSingleSelectComponent,
    OperationTypeSingleSelectComponent,
    FilialSingleSelectComponent,
    DocumentTypeSingleSelectComponent,
    CostCenterSingleSelectComponent,
    CurrencySingleSelectComponent,
    DatepickerComponent,
    CompanySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentTemplateFormComponent extends BaseFormComponent<DocumentTemplateFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public template = <DocumentTemplateFormInterface>{};
  public dto = DocumentTemplateFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];
  public today = moment().format('YYYY-MM-DD');

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly documentRegistryService: DocumentRegistryService,
    private readonly documentTemplateService: DocumentTemplateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly directionService: DirectionService,
    private readonly confirmService: ConfirmDialogService,
    public readonly accessService: AccessService,
    private readonly errorService: ErrorService,
    public readonly globalService: GlobalService
  ) {
    super();
  }

  public ngOnInit(): void {
    const documentId = this.formStoreService.getState.forms[this.index]?.data?.documentId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (documentId) {
      this.documentRegistryService.findById<DocumentFindInterface>(documentId).subscribe((document) => {
        this.template = <DocumentTemplateFindInterface>{
          amount: document.amount,
          company: document.company,
          contragent: document.contragent,
          type: document.documentType,
          filial: document.filial,
          unit: document.unit,
          direction: document.direction,
          deal: document.deal,
          commentary: document.commentary,
          costCenter: document.costCenter,
          currency: document.currency,
          planning: {
            periodicity: TemplateEntityPeriodicityEnum.NONE
          },
          deadline: {
            daysAfter: 0,
            type: DeadlineTypeEnum.RELATIVE
          }
        };
        this.cdRef.markForCheck();
      });
    }

    forkJoin([this.directionService.find<DirectionFindInterface>()]).subscribe(([directions]) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.template.planning = {
        periodicity: TemplateEntityPeriodicityEnum.NONE
      };

      this.template.deadline = {
        daysAfter: 0,
        type: DeadlineTypeEnum.RELATIVE
      };
    }
  }

  private getById() {
    this.formStoreService.setLoading(true, this.index);

    this.documentTemplateService
      .findById<DocumentTemplateFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe((template) => {
        this.template = template;
        this.cdRef.markForCheck();
        this.formStoreService.setLoading(false, this.index);
      });
  }

  public saveTemplate() {
    const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
    if (
      (requiredAnalyticalFeatures?.directionsRequired && !this.template.direction) ||
      (requiredAnalyticalFeatures?.unitsRequired && !this.template.unit) ||
      (requiredAnalyticalFeatures?.filialsRequired && !this.template.filial)
    ) {
      this.errors = Object.create({});

      if (requiredAnalyticalFeatures?.directionsRequired && !this.template.direction) {
        this.errors.direction = 'Бюджетная статья не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.unitsRequired && !this.template.unit) {
        this.errors.unit = 'Бюджетная единица не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.filialsRequired && !this.template.filial) {
        this.errors.filial = 'Филиал не выбран' as any;
      }
      return;
    }

    if (this.checkValidate(this.template).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.documentTemplateService
          .update<DocumentTemplateFormInterface, DocumentTemplateFindInterface>(this.template)
          .subscribe({
            next: (template) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Шаблон "${template.name}" обновлен!`);
              this.formStoreService.saveNext(template, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.documentTemplateService.create<DocumentTemplateFindInterface>(this.template).subscribe({
          next: (template) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Шаблон создан!');
            this.formStoreService.saveNext(template, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTemplate() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить шаблон "${this.template?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);

        this.documentTemplateService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Шаблон "${this.template.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public changeCompany() {
    this.currencies = [];
    this.template.currency = null;

    if (this.template.company?.currencies?.length) {
      this.currencies = this.template.company.currencies;
      setTimeout(() => {
        this.template.currency = this.currencies.find((currency) => currency.isDefault);
        this.cdRef.markForCheck();
      });
    }
  }
}
